import React, { useState } from 'react';
import { roomApi } from '../../services/api';
import { jiraApi } from '../../services/jiraApi';
import { QueueItem } from '../../types';
import { JiraIssue } from '../../types/jira';
import { apiErrorMessage } from '../../utils/apiError';

interface StoryQueuePanelProps {
  roomId: string;
  queue: QueueItem[];
  isModerator: boolean;
  onQueueChange: (queue: QueueItem[]) => void;
  /** Moderator-only: removes the item from the queue and starts a session for it. */
  onStart: (item: QueueItem) => void;
}

/**
 * Lets anyone in the room queue up stories ahead of time (manually, or from
 * JIRA search when configured); the moderator can then start the next
 * session from the queue's front instead of retyping the title each time.
 */
const StoryQueuePanel: React.FC<StoryQueuePanelProps> = ({
  roomId,
  queue,
  isModerator,
  onQueueChange,
  onStart,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');

  const [jiraQuery, setJiraQuery] = useState('');
  const [jiraResults, setJiraResults] = useState<JiraIssue[]>([]);
  const [isSearchingJira, setIsSearchingJira] = useState(false);
  const jiraEnabled = jiraApi.isEnabled();

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsAdding(true);
    setError('');

    try {
      const updated = await roomApi.addToQueue(roomId, {
        title: title.trim(),
        description: description.trim() || undefined,
      });
      onQueueChange(updated);
      setTitle('');
      setDescription('');
    } catch (err) {
      console.error('Failed to add to queue:', err);
      setError(apiErrorMessage(err, 'キューへの追加に失敗しました'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleJiraSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const config = jiraApi.getConfig();
    if (!config || !jiraQuery.trim()) return;

    setIsSearchingJira(true);
    setError('');

    try {
      const jql = jiraApi.generateSearchJql(config.projectKey, jiraQuery.trim());
      const result = await jiraApi.searchIssues(jql, 10);
      setJiraResults(result.issues);
    } catch (err) {
      console.error('Failed to search JIRA issues:', err);
      setError(err instanceof Error ? err.message : 'JIRA検索に失敗しました');
    } finally {
      setIsSearchingJira(false);
    }
  };

  const handleAddFromJira = async (issue: JiraIssue) => {
    setError('');

    try {
      const updated = await roomApi.addToQueue(roomId, { title: issue.summary, jira_key: issue.key });
      onQueueChange(updated);
      setJiraResults((prev) => prev.filter((i) => i.key !== issue.key));
    } catch (err) {
      console.error('Failed to add JIRA issue to queue:', err);
      setError(apiErrorMessage(err, 'キューへの追加に失敗しました'));
    }
  };

  const handleRemove = async (itemId: string) => {
    setError('');

    try {
      const updated = await roomApi.removeFromQueue(roomId, itemId);
      onQueueChange(updated);
    } catch (err) {
      console.error('Failed to remove from queue:', err);
      setError(apiErrorMessage(err, 'キューからの削除に失敗しました'));
    }
  };

  return (
    <div className="story-queue-panel card">
      <h3>ストーリーキュー</h3>

      {queue.length === 0 ? (
        <p className="queue-empty">キューは空です。次に見積もるストーリーを追加しておけます。</p>
      ) : (
        <ul className="queue-list">
          {queue.map((item) => (
            <li key={item.id} className="queue-item">
              <div className="queue-item-info">
                <span className="queue-item-title">
                  {item.title}
                  {item.jira_key && <span className="queue-item-jira-key">{item.jira_key}</span>}
                </span>
                {item.description && <span className="queue-item-description">{item.description}</span>}
              </div>
              {isModerator && (
                <div className="queue-item-actions">
                  <button onClick={() => onStart(item)} className="btn btn-primary">
                    この投票を開始
                  </button>
                  <button onClick={() => handleRemove(item.id)} className="btn btn-secondary">
                    削除
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleAdd} className="queue-add-form">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="ストーリータイトルを追加"
          className="form-input"
        />
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="説明（任意）"
          className="form-input"
        />
        <button type="submit" disabled={!title.trim() || isAdding} className="btn btn-secondary">
          {isAdding ? '追加中...' : 'キューに追加'}
        </button>
      </form>

      {jiraEnabled && (
        <div className="queue-jira-search">
          <form onSubmit={handleJiraSearch} className="queue-jira-search-form">
            <input
              type="text"
              value={jiraQuery}
              onChange={(e) => setJiraQuery(e.target.value)}
              placeholder="JIRAから検索してキューに追加"
              className="form-input"
            />
            <button
              type="submit"
              disabled={!jiraQuery.trim() || isSearchingJira}
              className="btn btn-secondary"
            >
              {isSearchingJira ? '検索中...' : '検索'}
            </button>
          </form>

          {jiraResults.length > 0 && (
            <ul className="queue-jira-results">
              {jiraResults.map((issue) => (
                <li key={issue.key} className="queue-jira-result-item">
                  <span className="queue-item-jira-key">{issue.key}</span>
                  <span className="queue-jira-result-summary">{issue.summary}</span>
                  <button onClick={() => handleAddFromJira(issue)} className="btn btn-secondary">
                    追加
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default StoryQueuePanel;
